// Import types from google.maps
/// <reference types="google.maps" />

// Function to calculate the distance between two points in meters using the Haversine formula
function haversineDistance(
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number,
	assumeFlat: boolean = false,
): number {
	const R = 6371000; // Radius of the Earth in meters
	const toRadians = (degree: number) => (degree * Math.PI) / 180;

	if (assumeFlat) {
		const x =
			(toRadians(lon2) - toRadians(lon1)) *
			Math.cos((toRadians(lat1) + toRadians(lat2)) / 2);
		const y = toRadians(lat2 - lat1);

		return Math.sqrt(x * x + y * y) * R; // Return simple calculation for flat surface, distance in meters
	}

	const dLat = toRadians(lat2 - lat1);
	const dLon = toRadians(lon2 - lon1);

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRadians(lat1)) *
			Math.cos(toRadians(lat2)) *
			Math.sin(dLon / 2) *
			Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	return R * c; // Distance in meters
}

interface Degrees {
	latitude: number;
	longitude: number;
}

function metersToDegrees(lat: number, sizeInMeters: number): Degrees {
	// One degree of latitude is approximately 111,000 meters
	const latDegrees = sizeInMeters / 111000;

	// One degree of longitude in meters varies with latitude
	const lngDegrees =
		sizeInMeters / (111000 * Math.cos((lat * Math.PI) / 180));

	return {
		latitude: latDegrees,
		longitude: lngDegrees,
	};
}

function getSquareSizeInDegrees(lat: number, sizeInMeters: number): Degrees {
	return metersToDegrees(lat, sizeInMeters);
}

function getSquareId(lat: number, lng: number, sizeInDegrees: Degrees): string {
	const latSquare = Math.floor(lat / sizeInDegrees.latitude);
	const lngSquare = Math.floor(lng / sizeInDegrees.longitude);
	return `${latSquare}_${lngSquare}`;
}

function getVisibleSquares(
	bounds: google.maps.LatLngBounds,
	squareSizeMeter: number,
	includeAdjacent: boolean = false,
): string[] {
	const visibleSquares = new Set<string>();

	const ne = bounds.getNorthEast();
	const sw = bounds.getSouthWest();
	const north = ne.lat();
	const east = ne.lng();
	const south = sw.lat();
	const west = sw.lng();

	// Determine the central latitude to compute degrees based on meters
	const centerLat = (north + south) / 2;

	const sizeInDegrees = getSquareSizeInDegrees(centerLat, squareSizeMeter);

	// Calculate the start and end points for latitude and longitude
	let latStart = Math.floor(south / sizeInDegrees.latitude);
	let latEnd = Math.floor(north / sizeInDegrees.latitude);
	let lngStart = Math.floor(west / sizeInDegrees.longitude);
	let lngEnd = Math.floor(east / sizeInDegrees.longitude);

	// If includeAdjacent is true, extend the range by 1 square in each direction
	if (includeAdjacent) {
		latStart -= 1;
		latEnd += 1;
		lngStart -= 1;
		lngEnd += 1;
	}

	// Iterate through the latitude and longitude ranges
	for (let lat = latStart; lat <= latEnd; lat++) {
		for (let lng = lngStart; lng <= lngEnd; lng++) {
			const squareId = `${lat}_${lng}`;
			visibleSquares.add(squareId);
		}
	}

	return Array.from(visibleSquares);
}

interface Bounds {
	north: number;
	south: number;
	east: number;
	west: number;
}

function getBoundsForSquare(squareId: string, squareSizeMeter: number): Bounds {
	const [latSquare, lngSquare] = squareId.split('_').map(Number);

	// Calculate approximate latitude for this square's center for accurate size in degrees
	const approximateLat = latSquare * (squareSizeMeter / 111000);

	const sizeInDegrees = metersToDegrees(approximateLat, squareSizeMeter);

	const latStart = latSquare * sizeInDegrees.latitude;
	const latEnd = latStart + sizeInDegrees.latitude;
	const lngStart = lngSquare * sizeInDegrees.longitude;
	const lngEnd = lngStart + sizeInDegrees.longitude;

	return {
		north: latEnd,
		south: latStart,
		east: lngEnd,
		west: lngStart,
	};
}

export {
	haversineDistance,
	metersToDegrees,
	getSquareSizeInDegrees,
	getSquareId,
	getVisibleSquares,
	getBoundsForSquare,
};
